import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { App } from "./App";
import { PwaUpdatePrompt } from "./components/PwaUpdatePrompt";
import { PwaInstallPrompt } from "./components/PwaInstallPrompt";
import "./lib/i18n"; // Initialize i18n before rendering
import "./styles/globals.css";

function AppErrorFallback({
  error,
  resetError,
}: {
  error: unknown;
  resetError: () => void;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-organize-bg p-8">
      <div className="text-center max-w-md">
        <AlertTriangle className="h-16 w-16 text-red-400 mx-auto mb-6" />
        <h1 className="text-xl font-bold text-vizimix-100 mb-2">
          Something went wrong
        </h1>
        <p className="text-sm text-vizimix-400 mb-6">
          The application encountered an unexpected error. Try again or refresh
          the page.
        </p>
        <div className="flex gap-3 justify-center">
          <button
            onClick={resetError}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-vizimix-600 text-white text-sm font-medium hover:bg-vizimix-500 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </button>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-vizimix-700 text-vizimix-300 text-sm font-medium hover:bg-vizimix-800 transition-colors"
          >
            Refresh Page
          </button>
        </div>
        {import.meta.env.DEV && (
          <details className="mt-8 text-left">
            <summary className="text-xs text-vizimix-500 cursor-pointer hover:text-vizimix-400">
              Error Details
            </summary>
            <pre className="mt-2 text-xs text-red-300/70 font-mono whitespace-pre-wrap overflow-auto max-h-48 bg-red-950/30 rounded p-3 border border-red-900/50">
              {error instanceof Error ? error.message : String(error)}
              {error instanceof Error && error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

// Initialize Sentry for error tracking
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.PROD ? "production" : "development",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

// In development, clean up any stale service workers from previous builds
if (!import.meta.env.PROD && "serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
      console.log("[PWA] Unregistered stale service worker (dev mode)");
    }
  });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <AppErrorFallback error={error} resetError={resetError} />
      )}
      beforeCapture={(scope) => {
        scope.setTag("boundary", "app");
      }}
    >
      <QueryClientProvider client={queryClient}>
        <PwaUpdatePrompt />
        <PwaInstallPrompt autoShow delay={10000} />
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>
);
